import { useState } from 'react'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { OrganisationNameLookupField } from '@/components/onboarding/organisation-name-lookup-field'
import { createManagedOrganisation } from '@/lib/employer-onboarding-organisation-api'

interface EmployerOrgNameStepProps {
  isCompleting: boolean
  onCreated: (slug: string) => void
}

/** First step of the "add organisation" journey — creates the org, then the wizard advances to bio/logo/prefs for the new slug. */
export function EmployerOrgNameStep({ isCompleting, onCreated }: EmployerOrgNameStepProps) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const busy = saving || isCompleting

  const handleContinue = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Organisation name is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const { slug } = await createManagedOrganisation(
        trimmed,
        domain ? { website: `https://${domain}` } : {},
      )
      onCreated(slug)
    } catch (err) {
      console.error('Error creating organisation:', err)
      setError(err instanceof Error ? err.message : 'Failed to create organisation')
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <OrganisationNameLookupField
        id="org-create-name"
        value={name}
        onChange={setName}
        domain={domain}
        onDomainChange={setDomain}
        onSubmit={() => void handleContinue()}
        disabled={busy}
        focusOnMount
      />

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <OnboardingStepActions
        onContinue={handleContinue}
        continueDisabled={busy || !name.trim()}
        isBusy={busy}
        continueBusyLabel="Creating…"
      />
    </div>
  )
}
